import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import ClientService from '../../Services/client.service';
import ClientDocumentCountService from '../../Services/ClientDocumentCount.service';
import AssignmentService from '../../Services/assignment.service';
import EnumsService from '../../Services/enums.service';
import ReportsService from '../../Services/reports.service';

// Async thunk for fetching all main page data
export const fetchMainPageData = createAsyncThunk(
  'mainPageData/fetchAll',
  async ({ accountantId, token, page, pageSize, date, isAccountant = false }, { rejectWithValue, getState }) => {
    try {
      console.log('🌐 Redux Thunk: fetchMainPageData called', { accountantId, page, isAccountant });
      
      const yearForMonth = date ? date.getUTCFullYear() : new Date().getUTCFullYear();
      const monthForMonth = date ? (date.getUTCMonth() + 1) : (new Date().getUTCMonth() + 1);

      // Fetch critical data in parallel
      const [clientsData, assignments, withholdingReportingResponse] = await Promise.all([
        ClientService.GetMinimalClients(accountantId, token, page, pageSize),
        AssignmentService.GetClientAssignmentSummary(accountantId, token),
        ClientService.GetWithholdingReportingForAccountantForMonth(
          accountantId,
          yearForMonth,
          monthForMonth,
          token
        ),
      ]);

      // Fetch additional data for accountant if needed
      let clientsDataForAccountant = null;
      if (isAccountant) {
        clientsDataForAccountant = await ClientService.GetMinimalClients(
          accountantId,
          token,
          page,
          pageSize,
          false
        );
      }

      // Get case IDs for balance queries (active + inactive accountant clients, incl. ManageOnly)
      const caseIds = [
        ...new Set([
          ...(clientsData?.clients || []).map((c) => c.caseId),
          ...(clientsDataForAccountant?.clients || []).map((c) => c.caseId),
        ].filter(Boolean)),
      ];

      // Fetch collection balances and report statuses in parallel
      const [vatStatusResponse, advanceStatusResponse, balances] = await Promise.all([
        ReportsService.isVatPeriodReportedForAccountant(accountantId, date, token),
        ReportsService.isAdvancePeriodReportedForAccountant(accountantId, date, token),
        EnumsService.GetBalancesByCaseIds(accountantId, caseIds, token, date),
      ]);

      return {
        clients: clientsData,
        clientsForAccountant: clientsDataForAccountant,
        assignments,
        balances,
        vatStatuses: vatStatusResponse.results || {},
        advanceStatuses: advanceStatusResponse.results || {},
        withholdingReportingStatuses: withholdingReportingResponse.results || {},
        accountantId,
        page,
        pageSize,
        date: date ? date.toISOString() : new Date().toISOString(),
        isAccountant,
        timestamp: Date.now(),
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching document counts separately (background)
export const fetchDocumentCountsForMainPage = createAsyncThunk(
  'mainPageData/fetchDocumentCounts',
  async ({ accountantId, token, page, pageSize, isAccountant = false }, { rejectWithValue, getState }) => {
    try {
      console.log('📊 Redux Thunk: fetchDocumentCounts called', { accountantId, page, isAccountant });
      
      const documentCounts = await ClientDocumentCountService.getTotalCountClientDocumentCountsByAccountantId(
        accountantId,
        token,
        page,
        pageSize,
        true
      );
      
      console.log('📊 Document counts fetched:', documentCounts?.length || 0, 'items');

      let documentCountsForAccountant = null;
      if (isAccountant) {
        documentCountsForAccountant = await ClientDocumentCountService.getTotalCountClientDocumentCountsByAccountantId(
          accountantId,
          token,
          page,
          pageSize,
          false
        );
      }

      return {
        documentCounts,
        documentCountsForAccountant,
        accountantId,
        timestamp: Date.now(),
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  // Main data storage by accountantId
  data: {},
  // Loading states by accountantId
  loading: {},
  // Document counts loading states
  documentCountsLoading: {},
  // Error states by accountantId
  errors: {},
  // Cache metadata
  cache: {},
  // Cache duration in milliseconds (2 minutes - shorter for frequently changing data)
  cacheDuration: 2 * 60 * 1000,
};

const mainPageDataSlice = createSlice({
  name: 'mainPageData',
  initialState,
  reducers: {
    // Clear cache for specific accountant
    clearMainPageCache: (state, action) => {
      const { accountantId } = action.payload || {};
      console.log('🗑️ Clearing main page cache:', accountantId || 'all');
      if (accountantId) {
        delete state.data[accountantId];
        delete state.cache[accountantId];
        delete state.loading[accountantId];
        delete state.errors[accountantId];
        delete state.documentCountsLoading[accountantId];
      } else {
        // Clear all cache
        state.data = {};
        state.cache = {};
        state.loading = {};
        state.errors = {};
        state.documentCountsLoading = {};
      }
    },
    // Force refresh - clears cache and triggers refetch
    forceRefresh: (state, action) => {
      const { accountantId } = action.payload || {};
      console.log('🔄 Force refresh triggered:', accountantId || 'all');
      if (accountantId) {
        delete state.data[accountantId];
        delete state.cache[accountantId];
      } else {
        state.data = {};
        state.cache = {};
      }
    },
    // Update specific client data (for real-time updates via SignalR)
    updateClientInMainPage: (state, action) => {
      const { accountantId, caseId, updates } = action.payload;

      const patchClientList = (list) => {
        if (!list) return;
        const clientIndex = list.findIndex((c) => c.caseId === caseId);
        if (clientIndex !== -1) {
          list[clientIndex] = { ...list[clientIndex], ...updates };
        }
      };

      patchClientList(state.data[accountantId]?.clients?.clients);
      patchClientList(state.data[accountantId]?.clientsForAccountant?.clients);

      if (updates.withholdingReporting !== undefined && state.data[accountantId]) {
        if (!state.data[accountantId].withholdingReportingStatuses) {
          state.data[accountantId].withholdingReportingStatuses = {};
        }
        state.data[accountantId].withholdingReportingStatuses[caseId] = updates.withholdingReporting;
      }
    },
    // Update document counts after background fetch
    updateDocumentCounts: (state, action) => {
      const { accountantId, documentCounts, documentCountsForAccountant } = action.payload;
      if (state.data[accountantId]) {
        state.data[accountantId].documentCounts = documentCounts;
        state.data[accountantId].documentCountsForAccountant = documentCountsForAccountant;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Main data fetching
      .addCase(fetchMainPageData.pending, (state, action) => {
        const { accountantId } = action.meta.arg;
        state.loading[accountantId] = true;
        state.errors[accountantId] = null;
      })
      .addCase(fetchMainPageData.fulfilled, (state, action) => {
        const { accountantId } = action.payload;
        state.data[accountantId] = action.payload;
        state.loading[accountantId] = false;
        state.cache[accountantId] = {
          timestamp: action.payload.timestamp,
          page: action.payload.page,
          pageSize: action.payload.pageSize,
          date: action.payload.date,
        };
      })
      .addCase(fetchMainPageData.rejected, (state, action) => {
        const { accountantId } = action.meta.arg;
        state.loading[accountantId] = false;
        state.errors[accountantId] = action.payload;
      })
      // Document counts fetching
      .addCase(fetchDocumentCountsForMainPage.pending, (state, action) => {
        const { accountantId } = action.meta.arg;
        state.documentCountsLoading[accountantId] = true;
      })
      .addCase(fetchDocumentCountsForMainPage.fulfilled, (state, action) => {
        const { accountantId, documentCounts, documentCountsForAccountant } = action.payload;
        if (state.data[accountantId]) {
          state.data[accountantId].documentCounts = documentCounts;
          state.data[accountantId].documentCountsForAccountant = documentCountsForAccountant;
          state.data[accountantId].documentCountsTimestamp = action.payload.timestamp;
        }
        state.documentCountsLoading[accountantId] = false;
      })
      .addCase(fetchDocumentCountsForMainPage.rejected, (state, action) => {
        const { accountantId } = action.meta.arg;
        state.documentCountsLoading[accountantId] = false;
      });
  },
});

// Selectors
export const selectMainPageData = (state, accountantId) =>
  state.mainPageDataSlice?.data[accountantId] || null;

export const selectMainPageLoading = (state, accountantId) =>
  state.mainPageDataSlice?.loading[accountantId] || false;

export const selectDocumentCountsLoading = (state, accountantId) =>
  state.mainPageDataSlice?.documentCountsLoading[accountantId] || false;

export const selectMainPageError = (state, accountantId) =>
  state.mainPageDataSlice?.errors[accountantId] || null;

export const selectIsMainPageCacheValid = (state, accountantId, currentDate) => {
  const cache = state.mainPageDataSlice?.cache[accountantId];
  const hasData = !!state.mainPageDataSlice?.data[accountantId];
  
  if (!cache || !hasData) {
    return false;
  }

  const now = Date.now();
  const cacheAge = now - cache.timestamp;
  
  // Check if cache is within duration (2 minutes)
  if (cacheAge >= state.mainPageDataSlice.cacheDuration) {
    return false;
  }
  
  // If no date provided or cached date missing, just check time
  if (!currentDate || !cache.date) {
    return true;
  }
  
  // Check if date matches (important for balance/report data)
  try {
  const cachedDate = new Date(cache.date);
    const currentDateObj = new Date(currentDate);
    
    // Compare year and month only (ignore day)
    const isSameMonth =
      cachedDate.getFullYear() === currentDateObj.getFullYear() &&
      cachedDate.getMonth() === currentDateObj.getMonth();

    return isSameMonth;
  } catch (error) {
    console.error('❌ Error validating cache date:', error);
    return false; // Invalid cache if date comparison fails
  }
};

export const { clearMainPageCache, updateClientInMainPage, updateDocumentCounts, forceRefresh } =
  mainPageDataSlice.actions;

export default mainPageDataSlice.reducer;

